import {
  WMU_ERROR_ALIASES,
  WMU_ERROR_CATALOG,
  WMU_ERROR_STATUS_DEFAULTS,
  type WMUErrorDescriptor,
} from "./generated/error_catalog";

export type BrowserErrorField = {
  field: string;
  message: string;
};

export type BrowserErrorDetail = {
  rawCode: string;
  code: string;
  message: string;
  status: number;
  retryAfter: number;
  fields: BrowserErrorField[];
  descriptor: WMUErrorDescriptor | null;
};

export type WMUErrorsAPI = {
  normalizeCode: (raw: unknown, status?: number) => string;
  parsePayload: (payload: unknown, responseLike?: { status?: number; statusText?: string }) => BrowserErrorDetail;
  parseHXTriggerHeader: (raw: unknown, status?: number) => BrowserErrorDetail | null;
  toastVariant: (detail: BrowserErrorDetail) => "error" | "warning" | "info";
  shouldRedirectToLogin: (detail: Pick<BrowserErrorDetail, "code" | "status">) => boolean;
  shouldRedirectCode: (raw: unknown, status?: number) => boolean;
  buildLoginReturnURL: (currentURL?: string) => string;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function safeString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function safeNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeRawCode(raw: unknown): string {
  return safeString(raw)
    .replace(/[\s-]+/g, "_")
    .toUpperCase();
}

function descriptorForCode(code: string): WMUErrorDescriptor | null {
  if (!code) {
    return null;
  }
  return Object.prototype.hasOwnProperty.call(WMU_ERROR_CATALOG, code)
    ? WMU_ERROR_CATALOG[code as keyof typeof WMU_ERROR_CATALOG]
    : null;
}

export function normalizeCode(raw: unknown, status = 0): string {
  const normalized = normalizeRawCode(raw);
  if (normalized && Object.prototype.hasOwnProperty.call(WMU_ERROR_CATALOG, normalized)) {
    return normalized;
  }
  if (normalized && Object.prototype.hasOwnProperty.call(WMU_ERROR_ALIASES, normalized)) {
    return WMU_ERROR_ALIASES[normalized as keyof typeof WMU_ERROR_ALIASES];
  }
  const byStatus = WMU_ERROR_STATUS_DEFAULTS[String(status) as keyof typeof WMU_ERROR_STATUS_DEFAULTS];
  if (normalized) {
    return normalized;
  }
  if (typeof byStatus === "string" && byStatus.trim()) {
    return byStatus.trim();
  }
  return "SYSTEM_INTERNAL";
}

function fallbackMessage(code: string, status = 0): string {
  const descriptor = descriptorForCode(code);
  if (descriptor?.default_message) {
    return descriptor.default_message;
  }
  if (status > 0) {
    return `Request failed (${status}).`;
  }
  return "Request failed.";
}

function fieldList(value: unknown): BrowserErrorField[] {
  if (Array.isArray(value)) {
    return value
      .map((entry) => asRecord(entry))
      .map((entry) => ({
        field: safeString(entry.field),
        message: safeString(entry.message),
      }))
      .filter((entry) => entry.message);
  }

  const record = asRecord(value);
  return Object.entries(record)
    .map(([field, message]) => ({
      field: safeString(field),
      message: safeString(message),
    }))
    .filter((entry) => entry.message);
}

export function parsePayload(
  payload: unknown,
  responseLike: { status?: number; statusText?: string } = {},
): BrowserErrorDetail {
  const root = asRecord(payload);
  const error = asRecord(root.error);
  const status = safeNumber(responseLike.status);
  const rawCode = safeString(error.code || root.code);
  const code = normalizeCode(rawCode, status);
  const fields = fieldList(root.errors).length > 0
    ? fieldList(root.errors)
    : fieldList(asRecord(root.data).errors).length > 0
      ? fieldList(asRecord(root.data).errors)
      : fieldList(root.fields).length > 0
        ? fieldList(root.fields)
        : fieldList(error.fields);
  const message = safeString(error.message || root.message) || fallbackMessage(code, status);
  const retryAfter = safeNumber(error.retry_after || root.retry_after);

  return {
    rawCode,
    code,
    message,
    status,
    retryAfter,
    fields,
    descriptor: descriptorForCode(code),
  };
}

export function parseHXTriggerHeader(raw: unknown, status = 0): BrowserErrorDetail | null {
  const header = safeString(raw);
  if (!header) {
    return null;
  }
  try {
    const decoded = asRecord(JSON.parse(header) as unknown);
    const payload = asRecord(decoded["wmu:error"]);
    if (Object.keys(payload).length === 0) {
      return null;
    }
    return parsePayload(payload, { status });
  } catch {
    return null;
  }
}

export function toastVariant(detail: BrowserErrorDetail): "error" | "warning" | "info" {
  const treatment = safeString(detail.descriptor?.ux_treatment);
  if (treatment === "background_wait") {
    return "info";
  }
  if (treatment === "retry" || treatment === "inline_form" || treatment === "redirect_login" || treatment === "refresh") {
    return "warning";
  }
  if (treatment === "operator_escalation") {
    return "error";
  }
  if (detail.status >= 500) {
    return "error";
  }
  return "warning";
}

export function shouldRedirectCode(raw: unknown, status = 0): boolean {
  const code = normalizeCode(raw, status);
  return safeString(descriptorForCode(code)?.ux_treatment) === "redirect_login";
}

export function shouldRedirectToLogin(detail: Pick<BrowserErrorDetail, "code" | "status">): boolean {
  return shouldRedirectCode(detail.code, detail.status);
}

export function buildLoginReturnURL(currentURL?: string): string {
  const next = safeString(currentURL)
    || `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const url = new URL("/auth/login", window.location.origin);
  if (next && next !== "/auth/login") {
    url.searchParams.set("return_to", next);
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

const api: WMUErrorsAPI = {
  normalizeCode,
  parsePayload,
  parseHXTriggerHeader,
  toastVariant,
  shouldRedirectToLogin,
  shouldRedirectCode,
  buildLoginReturnURL,
};

window.WMUErrors = api;
